import React, { ChangeEvent, useEffect, useState } from 'react';
import { makeStyles } from 'design-system/styles';
import _ from 'lodash';
import SmartLink from 'react-scale/corp/components/SmartLink/SmartLink';
import { INamedObjectTypes } from '@scale/shared/types/lookup';
import { ResponseLinterRule, ScriptLinterCondition } from '@scale/shared/types/linter';
import Pagination from 'react-scale/components/design-system/Pagination';
import { Button, Select, Option } from 'react-scale/components/design-system';
import SelectProjectHeader from 'corp/components/Projects/SelectProjectHeader';
import { LinterJsonViewer } from '../linter_config_docs/LinterConfigDocsComponents';
import { IProjectJson } from '@scale/shared/types/project';
import moment, { Moment } from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import DatePicker from 'react-datepicker-legacy';
import { changeTimezone } from 'react-scale/dashboard.scale.com/lib/time';
import { Timezone } from '@scale/shared/defaults';
import Spinner from 'design-system/CircularProgress';
import { ResponseLinterLog, RuleWithLogStatus } from '@scale/shared/types/api/linting';

const DEFAULT_DATE = '2022-01-01T10:00:00';
const DATE_FORMAT = 'M/DD/YY';
const SCRIPT_NAME_REGEX = new RegExp('SCRIPT_|SCRIPT-', 'gi');

type Props = {
  from?: moment.Moment;
  setFrom: (to: moment.Moment) => void;
  className?: string;
  small?: boolean;
  style?: React.CSSProperties;
  disabled?: boolean;
};

const ResponseLinterLogs = ({
  linterLogs,
  selectedProject,
  onSelectProject,
}: {
  linterLogs: ResponseLinterLog[];
  selectedProject: IProjectJson;
  onSelectProject: (item?: { value: string }) => void;
}) => {
  const rulesName = new Set<string>();
  linterLogs?.forEach(log => log.rules.forEach(rule => rulesName.add(rule.name as string)));
  const rulesScriptname = new Set<string>();
  linterLogs.forEach(log =>
    log.rules.forEach(rule =>
      rulesScriptname.add((rule as ResponseLinterRule).condition.scriptName),
    ),
  );
  const [editedLogs, setEditedLogs] = useState<ResponseLinterLog[]>(_.cloneDeep(linterLogs));
  const [logsPerPage, setLogsPerPage] = useState<ResponseLinterLog[]>([]);
  const [count, setCount] = useState<number>(1);
  const [page, setPage] = useState<number>(1);
  const [filteredBy, setFilteredBy] = useState<{
    from: string;
    to: string;
    linterName: string;
    linterScriptName: string;
  }>({
    from: '',
    to: '',
    linterName: '',
    linterScriptName: '',
  });
  const [isFound, setIsFound] = useState<boolean>(true);
  const [messageError, setMessageError] = useState<string>('');
  const classes = useStyles();

  useEffect(() => {
    if (editedLogs.length) {
      const count = Math.round([...editedLogs].length / 10);
      setLogsPerPage(editedLogs?.slice(0, 10));
      setCount(count);
    }
  }, [editedLogs]);

  useEffect(() => {
    const start = (page - 1 === 0 ? 0 : page - 1) * 10;
    const end = page * 10;
    const logsPerPage = [...editedLogs].slice(start, end);
    setLogsPerPage(logsPerPage);
  }, [page]);

  useEffect(() => {
    getFormattedLogs(editedLogs);
  }, []);

  // TODO: move this process to the endpoint
  const getFormattedLogs = (logs: ResponseLinterLog[]) => {
    const rulesByIndex: Record<number, RuleWithLogStatus[]> = {};
    for (let i = 0; i < linterLogs.length; i++) {
      // all the liters are new at first log
      if (!linterLogs[i + 1]) {
        rulesByIndex[i] = linterLogs[i].rules.map(rule => ({ ...rule, logStatus: 'created' }));
        break;
      }

      const currentRules = [...linterLogs[i]?.rules];
      const previousRules = [...linterLogs[i + 1]?.rules];

      let customizeRules: RuleWithLogStatus[] = [];

      // `deletedRules` is initialized with all the previous rules
      // then, the rules that haven't been deleted will be removed from the array
      let deletedRules: RuleWithLogStatus[] = [...previousRules];

      for (const rule of currentRules) {
        const currentRule = _.cloneDeep(rule);
        const clonedPrevRules = _.cloneDeep(previousRules);

        const previousLog = _.find(clonedPrevRules, rule => rule.name === currentRule.name);
        if (!previousLog) {
          customizeRules = [...customizeRules, { ...currentRule, logStatus: 'created' }];
          continue;
        }

        // if the prev log was found, removes it from the deleted list
        deletedRules = _.filter(deletedRules, rule => rule.name !== previousLog.name);

        // creates an object with the updated items e.g. { enableL1: false, frontendSeverity: 'ERROR' };
        const updatedItems = _.pickBy(
          currentRule,
          (value, key) => !_.isEqual(value, previousLog[key as keyof ResponseLinterRule]),
        );

        const finalObject = { ...rule } as any;

        _.forEach(updatedItems, (_value, field) => {
          finalObject[field] = {
            new: updatedItems[field],
            old: (previousLog as any)[field],
          };
        });

        if (!_.isEmpty(updatedItems)) {
          customizeRules = [...customizeRules, { ...finalObject, logStatus: 'updated' }];
        }
      }

      rulesByIndex[i] = _.concat(
        customizeRules,
        deletedRules.map(rule => ({ ...rule, logStatus: 'deleted' })),
      );
    }

    setEditedLogs(
      logs
        .map((log, ix) => {
          if (!rulesByIndex[ix]) return log;
          const rules = rulesByIndex[ix].filter(rule => !_.isEmpty(rule));
          return {
            ...log,
            rules,
          };
        })
        .filter(log => log.rules.length),
    );
  };

  const changePage = (value: number) => {
    setPage(value);
    window.scrollTo(0, 0);
  };

  const filterLogs = () => {
    const { from, to, linterName, linterScriptName } = filteredBy;
    let filteredLogs = _.cloneDeep(editedLogs);
    if (from && to) {
      filteredLogs = filteredLogs.filter(log => {
        const dateToCheck = Date.parse(
          (_.isString(log.updatedAt) ? new Date(log.updatedAt) : log.updatedAt).toISOString(),
        );
        const from = Date.parse(filteredBy.from);
        const to = Date.parse(filteredBy.to);
        if (dateToCheck >= from && dateToCheck <= to) {
          return log;
        }
      });
    }
    if (linterName) {
      filteredLogs = filteredLogs
        .map(log => {
          return {
            ...log,
            rules: log.rules.filter(
              rule => (rule.name as string).toLowerCase() === linterName.toLowerCase(),
            ),
          };
        })
        .filter(log => log.rules.length);
    }
    if (linterScriptName) {
      filteredLogs = filteredLogs
        .map(log => {
          return {
            ...log,
            rules: log.rules
              .filter(rule => (rule as ResponseLinterRule).condition)
              .filter(rule => {
                const ruleCondition = (rule as RuleWithLogStatus).condition;
                const foundCondition =
                  'new' in ruleCondition && ruleCondition.new
                    ? ruleCondition.new.scriptName
                    : (ruleCondition as ScriptLinterCondition<any>).scriptName;
                return foundCondition === linterScriptName;
              }),
          };
        })
        .filter(log => log.rules.length);
    }
    if (filteredLogs.length) {
      setEditedLogs(filteredLogs);
    } else {
      setIsFound(false);
      setMessageError(`Logs not found`);
    }
  };

  const onClear = () => {
    setFilteredBy({
      from: '',
      to: '',
      linterName: '',
      linterScriptName: '',
    });
    setIsFound(true);
    getFormattedLogs(_.cloneDeep(linterLogs));
  };

  const getJSONfile = () => {
    const jsonStr = JSON.stringify(editedLogs);
    const blob = new Blob([jsonStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'response-linter-logs.json';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div style={{ maxWidth: '95%' }}>
      <h2>Response Linters Logs Info</h2>
      <SelectProjectHeader selectedProject={selectedProject} onSelectProject={onSelectProject} />
      {editedLogs.length ? (
        <div className={classes.logsContainer}>
          <div className={classes.searchContainer}>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <div style={{ display: 'flex', justifyContent: 'start', alignItems: 'center' }}>
                <div style={{ display: 'flex', flexDirection: 'column', marginRight: '10px' }}>
                  <label className={classes.dateLabel}>From</label>
                  <DatePickerContainer
                    className={classes.dateRangePickerDate}
                    from={moment(filteredBy.from || DEFAULT_DATE)}
                    setFrom={(value: Moment) =>
                      setFilteredBy({ ...filteredBy, from: value.toISOString() })
                    }
                  />
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', marginRight: '10px' }}>
                  <label className={classes.dateLabel}>To</label>
                  <DatePickerContainer
                    className={classes.dateRangePickerDate}
                    from={moment(filteredBy.to || DEFAULT_DATE)}
                    setFrom={(value: Moment) =>
                      setFilteredBy({ ...filteredBy, to: value.toISOString() })
                    }
                  />
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', marginRight: '10px' }}>
                  <label className={classes.dateLabel}>Linter Name</label>
                  <Select
                    variant="filled"
                    value={filteredBy.linterName}
                    renderValue={(value: any) => (!value ? 'Select...' : value)}
                    onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                      setFilteredBy({ ...filteredBy, linterName: e.target.value });
                    }}
                    classes={{ root: classes.selectStyle }}
                    style={{
                      padding: '1px',
                      borderRadius: '8px',
                      background: 'var(--bg-form-input)',
                    }}
                  >
                    {Array.from(rulesName)
                      .sort((a, b) => a.localeCompare(b))
                      .map((name, ix) => (
                        <Option value={name} key={`${ix}-${name}`}>
                          {name}
                        </Option>
                      ))}
                  </Select>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', marginRight: '10px' }}>
                  <label className={classes.dateLabel}>Linter Script Name</label>
                  <Select
                    variant="filled"
                    value={filteredBy.linterScriptName}
                    displayEmpty
                    renderValue={(value: any) => (!value ? 'Select...' : value)}
                    onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                      setFilteredBy({ ...filteredBy, linterScriptName: e.target.value });
                    }}
                    classes={{ root: classes.selectStyle }}
                    style={{
                      padding: '1px',
                      borderRadius: '8px',
                      background: 'var(--bg-form-input)',
                    }}
                  >
                    {Array.from(rulesScriptname)
                      .sort((a, b) =>
                        a
                          .replaceAll(SCRIPT_NAME_REGEX, '')
                          .localeCompare(b.replaceAll(SCRIPT_NAME_REGEX, '')),
                      )
                      .map((name, ix) => (
                        <Option value={name} key={`${ix}-${name}`}>
                          {name}
                        </Option>
                      ))}
                  </Select>
                </div>

                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    marginRight: '10px',
                    marginTop: '20px',
                  }}
                >
                  <Button onClick={filterLogs}>Apply Filters</Button>
                  <Button onClick={onClear} style={{ marginLeft: '10px' }}>
                    Clear Filters
                  </Button>
                </div>
              </div>

              <Button type="primary" onClick={getJSONfile} className={classes.downloadButton}>
                Download JSON file
              </Button>
            </div>
            <div>
              <Pagination size={'small'} page={page} count={count} onChange={changePage} />
            </div>
          </div>
          <div>
            {!isFound ? (
              <div>{messageError}</div>
            ) : logsPerPage.length ? (
              <>
                {logsPerPage.map((log, index) => (
                  <ProjectLinterDetails log={log} key={index} />
                ))}
                <Pagination size={'small'} page={page} count={count} onChange={changePage} />
              </>
            ) : (
              <Spinner />
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};

const ProjectLinterDetails = ({ log }: { log: ResponseLinterLog }) => {
  const classes = useStyles();
  return (
    <React.Fragment>
      <svg height={40} width={'100%'} style={{ marginTop: '30px' }}>
        <line x1="100%" className={classes.line} />
      </svg>
      {log.rules.length ? (
        <div style={{ marginBottom: '5px' }}>
          <div
            style={{
              marginBottom: '10px',
              display: 'flex',
              justifyContent: 'start',
              alignItems: 'center',
            }}
          >
            <div style={{ marginRight: '5px' }}>
              {`${log.rules.length} changes made on ${(_.isString(log.updatedAt)
                ? new Date(log.updatedAt)
                : log.updatedAt
              ).toLocaleDateString('en-US')}, by `}
            </div>
            <div>
              <SmartLink id={log.userId} type={INamedObjectTypes.User} linkText={log.userEmail} />
            </div>
          </div>
          <div className={classes.logsItem}>
            {log.rules.map((rule, ix) => (
              <div className={classes.ruleItem} key={ix}>
                <div
                  className={classes.tag}
                  style={{
                    background: `${
                      rule.logStatus === 'created'
                        ? '#72CE7B'
                        : rule.logStatus === 'deleted'
                        ? '#EF3D57'
                        : '#BBDEF2'
                    }`,
                  }}
                >
                  {rule.logStatus === 'created'
                    ? 'New Linter'
                    : rule.logStatus === 'deleted'
                    ? 'Deleted'
                    : 'Updated'}
                </div>
                <LinterJsonViewer
                  src={rule.logStatus === 'deleted' ? {} : rule}
                  name={log.rules[ix].name as string}
                  theme="monokai"
                  iconStyle="triangle"
                  collapsed={2}
                  collapseStringsAfterLength={50}
                  groupArraysAfterLength={false}
                  displayObjectSize={false}
                  displayDataTypes={false}
                  style={{
                    borderRadius: '5px',
                    padding: '10px',
                    minWidth: '413px',
                    minHeight: '500px',
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </React.Fragment>
  );
};

const DatePickerContainer = (props: Props) => {
  const { style, setFrom, from } = props;
  const today = changeTimezone(moment(), Timezone.UTC);
  const classes = useStyles();

  return (
    <div className={classes.datePicker} style={style}>
      <div className={classes.dateRangePickerContainer}>
        <FontAwesomeIcon
          icon="calendar"
          style={{ margin: '0 6px 0 14px', color: 'var(--color-primary)' }}
        />
        <DatePicker
          dateFormat={DATE_FORMAT}
          onChange={setFrom}
          selected={from}
          maxDate={today}
          className={classes.dateRangePickerDate}
        />
      </div>
    </div>
  );
};

const useStyles = makeStyles(theme => ({
  label: {
    fontFamily: 'arial',
    color: theme.colors.coolGray50,
  },
  selectStyle: {
    paddingTop: '6px',
    paddingBottom: '6px',
    paddingLeft: '10px',
    color: 'var(--color-form-input)',
    width: '160px',
    borderRadius: '8px',
    background: 'transparent',
  },

  inputStyle: {
    color: 'var(--color-form-input)',
    padding: '8px',
    width: '140px',
    alignItems: 'center',
    fontSize: '13px',
    borderRadius: '8px',
    background: 'var(--bg-form-input)',
    border: 0,
    margin: 0,
  },

  dateRangePickerDate: {
    cursor: 'pointer',
    color: 'var(--color-form-input)',
    padding: '8px',
    width: '140px',
    alignItems: 'center',
    fontSize: '13px',
    borderRadius: '8px',
    background: 'transparent',
    border: 0,
    textAlign: 'center',
    appearance: 'none',
    margin: 0,
  },

  dateRangePickerContainer: {
    alignItems: 'center',
    display: 'flex',
    borderRadius: '8px',
    background: 'var(--bg-form-input)',
    paddingRight: '6px',
  },

  datePicker: {
    display: 'flex',
    alignItems: 'end',
  },
  downloadButton: {
    width: '180px',
    marginTop: '20px',
  },
  dateLabel: {
    fontSize: 'small',
    marginBottom: '5px',
    fontFamily: 'arial',
    color: theme.colors.coolGray50,
  },
  searchContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'end',
    marginBottom: '10px',
  },
  textInput: {
    width: '180px',
    marginRight: '10px',
  },
  tag: {
    fontSize: 'medium',
    font: 'arial',
    padding: '5px',
    width: '25%',
    height: '30px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: '15px',
    marginBottom: '5px',
  },
  line: { stroke: theme.colors.coolGray20, strokeWidth: '5px' },
  goBack: {
    color: theme.colors.primary1,
    marginRight: '10px',
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
  },
  wrapper: {
    display: 'flex',
    width: '100%',
    maxWidth: 1240,
    margin: '0 auto',
  },
  logsContainer: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  logsItem: {
    display: 'flex',
    flexDirection: 'row',
    justifyContatent: 'space-between',
    overflowX: 'auto',
    overflowY: 'hidden',
    whitespace: 'nowrap',
  },
  ruleItem: {
    margin: '20px 10px 20px 10px',
    display: 'inline-block',
  },
  rawContainer: {
    height: '100%',
    flex: 1,
    margin: '0 12px',
  },
}));

export default ResponseLinterLogs;
